import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTerritoryStore } from '../../../store/territoryStore';
import { useUserStore } from '../../../store/userStore';
import type { TerritoryUpdate } from '../../../types';

interface TerritoryEditorProps {
  territoryId: string;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC',
];

export default function TerritoryEditor({ territoryId }: TerritoryEditorProps) {
  const { territories, regions, updateTerritory, replaceBoundaries } = useTerritoryStore();
  const { users, fetchUsers } = useUserStore();

  const territory = territories.find((t) => t.id === territoryId);

  const [name, setName] = useState('');
  const [regionId, setRegionId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [states, setStates] = useState<string[]>([]);
  const [zipInput, setZipInput] = useState('');
  const [zipCodes, setZipCodes] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Sync form state when territory changes
  useEffect(() => {
    if (territory) {
      setName(territory.name);
      setRegionId(territory.regionId);
      setAssignedUserId(territory.assignedUserId ?? '');
      setStates(territory.states?.map((s) => s.stateCode) ?? []);
      setZipCodes(territory.zipCodes?.map((z) => z.zipCode) ?? []);
      setZipInput('');
      setError(null);
      setSuccessMsg(null);
    }
  }, [territoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!territory) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">Territory not found.</p>
      </div>
    );
  }

  const isState = territory.territoryType === 'STATE';
  const isZip = territory.territoryType === 'ZIP_CODE';

  const toggleState = (code: string) => {
    setStates((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code].sort()
    );
  };

  const removeState = (code: string) => {
    setStates((prev) => prev.filter((s) => s !== code));
  };

  // Display ZIP prefix: "90000" → "900xx", "90001" stays as "90001"
  const formatZip = (zip: string) => zip.endsWith('00') ? `${zip.substring(0, 3)}xx` : zip;

  const addZips = () => {
    const newZips = zipInput
      .split(/[\s,]+/)
      .map((z) => z.trim())
      .filter(Boolean)
      .map((z) => {
        // Accept 3-digit prefix → expand to "XXX00"
        if (/^\d{3}$/.test(z)) return `${z}00`;
        // Accept 5-digit ZIP as-is
        if (/^\d{5}$/.test(z)) return z;
        return null;
      })
      .filter((z): z is string => z !== null && !zipCodes.includes(z));
    if (newZips.length > 0) {
      setZipCodes((prev) => [...prev, ...newZips]);
    }
    setZipInput('');
  };

  const removeZip = (zip: string) => {
    setZipCodes((prev) => prev.filter((z) => z !== zip));
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMsg(null);

    if (!name.trim()) return setError('Name is required.');
    if (!regionId) return setError('Region is required.');

    setIsSaving(true);
    try {
      const update: TerritoryUpdate = {
        name: name.trim(),
        regionId,
        assignedUserId: assignedUserId || null,
      };
      await updateTerritory(territoryId, update);

      if (isState) {
        await replaceBoundaries(territoryId, { states });
      } else if (isZip) {
        await replaceBoundaries(territoryId, { zipCodes });
      }

      setSuccessMsg('Territory saved.');
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Failed to save territory.');
    } finally {
      setIsSaving(false);
    }
  };

  const activeRegions = regions.filter((r) => r.isActive);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Edit Territory</h3>
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
          {territory.territoryType === 'ZIP_CODE' ? 'ZIP' : territory.territoryType}
        </span>
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </p>
      )}
      {successMsg && (
        <p className="text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded px-3 py-2">
          {successMsg}
        </p>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Region <span className="text-red-500">*</span>
          </label>
          <select
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select region...</option>
            {activeRegions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assigned Rep</label>
          <select
            value={assignedUserId}
            onChange={(e) => setAssignedUserId(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Unassigned</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName ?? u.email}
              </option>
            ))}
          </select>
        </div>

        {isState && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">States</label>

            {states.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {states.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                  >
                    {code}
                    <button
                      type="button"
                      onClick={() => removeState(code)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-200 ml-0.5"
                      aria-label={`Remove ${code}`}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-1">
              {US_STATES.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleState(code)}
                  className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                    states.includes(code)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
        )}

        {isZip && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              ZIP Prefixes
              <span className="font-normal text-gray-400 dark:text-gray-500 ml-1">
                ({zipCodes.length} added)
              </span>
            </label>

            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addZips();
                  }
                }}
                placeholder="Enter 3-digit prefixes or 5-digit ZIPs (comma separated)"
                className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addZips}
                className="border border-gray-300 dark:border-gray-600 px-3 py-2 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
              >
                Add
              </button>
            </div>

            {zipCodes.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 max-h-40 overflow-y-auto dark:bg-gray-700/50">
                <div className="flex flex-wrap gap-1">
                  {zipCodes.map((zip) => (
                    <span
                      key={zip}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                    >
                      {formatZip(zip)}
                      <button
                        type="button"
                        onClick={() => removeZip(zip)}
                        className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-200"
                        aria-label={`Remove ${formatZip(zip)}`}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-60"
        >
          {isSaving ? 'Saving...' : 'Save Territory'}
        </button>
      </div>
    </div>
  );
}
